import { createSlice, prepareAutoBatched, PayloadAction } from '@reduxjs/toolkit';
import { EUserNotificationType, TUserNotification } from '../api-ducks/user-notifications-duck/types-user-notifications';

export enum EBannerAppearance {
  Info = 'info',
  Notice = 'notice',
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
}

export enum EBannerType {
  UserNotification = 'user-notification',
  PageError = 'page-error',
}

export enum EBannerSource {
  ActionCenter = 'action-center',
  CustomNotifications = 'custom-notifications',
}

export type TBannerMessage = {
  id: string;
  message: string;
  appearance: EBannerAppearance;
  bannerType: EBannerType;
  source?: EBannerSource;
};

export interface BannerMessageState {
  [key: string]: TBannerMessage;
}

const notificationContentMapper = (notification: TUserNotification): string => {
  switch (notification.notificationType) {
    case EUserNotificationType.Banner:
      return notification.richMessage || '';
    case EUserNotificationType.ActionCenter:
      return notification.message || '';
    default:
      return notification.message || ''; // Backwards compatibility before notificationType was added
  }
};

const notificationSourceMapper = (notification: TUserNotification): EBannerSource | undefined => {
  switch (notification.notificationType) {
    case EUserNotificationType.ActionCenter:
      return EBannerSource.ActionCenter;
    case EUserNotificationType.Banner:
      return EBannerSource.CustomNotifications;
    default:
      return undefined;
  }
};

const initialState: BannerMessageState = {};
const createBannerMessageState = (message: string, appearance: EBannerAppearance, bannerType: EBannerType): BannerMessageState => {
  const id = `${message}:${appearance}`;
  return { [id]: { id, message, appearance, bannerType } };
};

const bannerMessagesDuck = createSlice({
  name: 'bannersMessages',
  initialState,
  reducers: {
    addNotification: (
      state,
      action: PayloadAction<{
        notification: TUserNotification;
        bannerType: EBannerType;
        appearance: EBannerAppearance;
      }>
    ) => ({
      ...state,
      [action.payload.notification.notificationReferenceId]: {
        id: action.payload.notification.notificationReferenceId,
        message: notificationContentMapper(action.payload.notification),
        appearance: action.payload.appearance,
        bannerType: action.payload.bannerType,
        source: notificationSourceMapper(action.payload.notification),
      },
    }),
    addInfo: (state, action: PayloadAction<{ message: string; bannerType: EBannerType }>) => ({
      ...state,
      ...createBannerMessageState(action.payload.message, EBannerAppearance.Info, action.payload.bannerType),
    }),
    addNotice: (state, action: PayloadAction<{ message: string; bannerType: EBannerType }>) => ({
      ...state,
      ...createBannerMessageState(action.payload.message, EBannerAppearance.Notice, action.payload.bannerType),
    }),
    addSuccess: (state, action: PayloadAction<{ message: string; bannerType: EBannerType }>) => ({
      ...state,
      ...createBannerMessageState(action.payload.message, EBannerAppearance.Success, action.payload.bannerType),
    }),
    addWarning: (state, action: PayloadAction<{ message: string; bannerType: EBannerType }>) => ({
      ...state,
      ...createBannerMessageState(action.payload.message, EBannerAppearance.Warning, action.payload.bannerType),
    }),
    addError: (state, action: PayloadAction<{ message: string; bannerType: EBannerType }>) => ({
      ...state,
      ...createBannerMessageState(action.payload.message, EBannerAppearance.Error, action.payload.bannerType),
    }),
    clearBannerMessage: {
      reducer: (state, action: PayloadAction<string>) => {
        if (state[action.payload]) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [action.payload]: unused, ...newState } = state;
          return newState;
        }
        return state;
      },
      prepare: prepareAutoBatched<string>(),
    },
    clearBannerMessagesByType: {
      reducer: (state, action: PayloadAction<EBannerType>) =>
        Object.fromEntries(Object.entries(state).filter(([, value]) => value.bannerType !== action.payload)),
      prepare: (bannerType: EBannerType) => ({ payload: bannerType }),
    },
    clearBannerMessages: () => initialState,
  },
});

export const {
  addNotification,
  addInfo,
  addNotice,
  addSuccess,
  addWarning,
  addError,
  clearBannerMessage,
  clearBannerMessagesByType,
  clearBannerMessages,
} = bannerMessagesDuck.actions;

export default bannerMessagesDuck.reducer;
